import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowDownToLine,
  Check,
  XCircle,
  Clock,
  PoundSterling,
  Landmark,
} from "lucide-react";
import { useState } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatMoney } from "@/lib/utils";

interface AdminWithdrawal {
  id: string;
  userId: string;
  amount: string;
  status: string;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  bankName: string | null;
  paidAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  userMarket?: string | null;
  user?: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    market?: string | null;
  };
}

function formatCurrency(val: string | number, market?: string | null) {
  const num = typeof val === "string" ? parseFloat(val) : val;
  return formatMoney(num, market);
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatBankCode(code: string, market?: string | null) {
  const clean = code.replace(/[^0-9]/g, "");
  if (market === "au") {
    // BSB is XXX-XXX
    if (clean.length === 6) return `${clean.slice(0,3)}-${clean.slice(3,6)}`;
    return clean;
  }
  if (market === "ca") {
    // Canadian transit (5) + institution (3) = XXXXX-XXX
    if (clean.length === 8) return `${clean.slice(0,5)}-${clean.slice(5,8)}`;
    return clean;
  }
  if (market === "us") {
    // ABA Routing Number is exactly 9 digits, displayed as a single block.
    return clean;
  }
  // UK Sort Code is XX-XX-XX
  if (clean.length === 6) return `${clean.slice(0,2)}-${clean.slice(2,4)}-${clean.slice(4,6)}`;
  return clean;
}
function bankCodeLabelFor(market?: string | null) {
  if (market === "au") return "BSB";
  if (market === "ca") return "Transit / Institution";
  if (market === "us") return "ABA Routing";
  return "Sort Code";
}

function getUserDisplayName(user?: { firstName: string | null; lastName: string | null; email: string | null }) {
  if (!user) return "Unknown User";
  const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return name || user.email || "Unknown User";
}

type TabFilter = "pending" | "paid" | "rejected" | "all";

export default function AdminWithdrawalsPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<TabFilter>("pending");
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data: withdrawals, isLoading } = useQuery<AdminWithdrawal[]>({
    queryKey: ["/api/admin/withdrawals"],
  });

  const payMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("PATCH", `/api/admin/withdrawals/${id}/pay`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Withdrawal marked as paid" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/withdrawals"] });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to mark as paid", variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await apiRequest("PATCH", `/api/admin/withdrawals/${id}/reject`, { reason });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Withdrawal rejected", description: "The amount has been refunded to the user's wallet." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/withdrawals"] });
      setRejectingId(null);
      setRejectReason("");
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to reject withdrawal", variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const all = withdrawals || [];
  const pending = all.filter(w => w.status === "pending");
  const paid = all.filter(w => w.status === "paid");
  const rejected = all.filter(w => w.status === "rejected");

  const pendingTotal = pending.reduce((sum, w) => sum + parseFloat(w.amount), 0);
  const paidTotal = paid.reduce((sum, w) => sum + parseFloat(w.amount), 0);

  const filtered = activeTab === "all" ? all :
    activeTab === "pending" ? pending :
    activeTab === "paid" ? paid : rejected;

  const tabs: { key: TabFilter; label: string; count: number }[] = [
    { key: "pending", label: "Pending", count: pending.length },
    { key: "paid", label: "Paid", count: paid.length },
    { key: "rejected", label: "Rejected", count: rejected.length },
    { key: "all", label: "All", count: all.length },
  ];

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-lg sm:text-2xl font-bold" data-testid="text-admin-withdrawals-title">Withdrawal Requests</h1>
        <p className="text-muted-foreground mt-1">Review and process withdrawal requests from users.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="card-stat-pending-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Requests</p>
                <p className="text-2xl font-bold" data-testid="text-pending-count">{pending.length}</p>
                <p className="text-sm text-muted-foreground">{formatCurrency(pendingTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="card-stat-paid-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <Check className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Paid Out</p>
                <p className="text-2xl font-bold" data-testid="text-paid-total">{formatCurrency(paidTotal)}</p>
                <p className="text-sm text-muted-foreground">{paid.length} withdrawals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="card-stat-total-requests">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <ArrowDownToLine className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Requests</p>
                <p className="text-2xl font-bold" data-testid="text-total-requests">{all.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {tabs.map(tab => (
          <Button
            key={tab.key}
            variant={activeTab === tab.key ? "default" : "outline"}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`button-tab-${tab.key}`}
          >
            {tab.label} ({tab.count})
          </Button>
        ))}
      </div>

      <Card data-testid="card-withdrawals-list">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Landmark className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No {activeTab === "all" ? "" : activeTab} withdrawal requests</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-4 font-medium text-muted-foreground">User</th>
                    <th className="p-4 font-medium text-muted-foreground">Amount</th>
                    <th className="p-4 font-medium text-muted-foreground">Bank Details</th>
                    <th className="p-4 font-medium text-muted-foreground">Date</th>
                    <th className="p-4 font-medium text-muted-foreground">Status</th>
                    <th className="p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((w) => (
                    <tr key={w.id} className="border-b last:border-0" data-testid={`row-admin-withdrawal-${w.id}`}>
                      <td className="p-4">
                        <div className="font-medium">{getUserDisplayName(w.user)}</div>
                        {w.user?.email && <div className="text-xs text-muted-foreground">{w.user.email}</div>}
                      </td>
                      <td className="p-4 font-semibold">{formatCurrency(w.amount, w.user?.market)}</td>
                      <td className="p-4">
                        <div className="text-xs space-y-0.5">
                          <div>{w.accountName}{w.bankName ? ` (${w.bankName})` : ""}</div>
                          <div className="font-mono text-muted-foreground">
                            <span className="text-muted-foreground">{bankCodeLabelFor(w.user?.market)}:</span>{" "}
                            {formatBankCode(w.sortCode, w.user?.market)} / {w.accountNumber}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">{formatDate(w.createdAt)}</td>
                      <td className="p-4">
                        <Badge variant={w.status === "paid" ? "default" : w.status === "rejected" ? "destructive" : "secondary"}>
                          {w.status === "pending" ? "Pending" : w.status === "paid" ? "Paid" : "Rejected"}
                        </Badge>
                        {w.status === "paid" && w.paidAt && (
                          <div className="text-xs text-muted-foreground mt-1">{formatDate(w.paidAt)}</div>
                        )}
                        {w.status === "rejected" && w.rejectionReason && (
                          <div className="text-xs text-destructive mt-1">{w.rejectionReason}</div>
                        )}
                      </td>
                      <td className="p-4">
                        {w.status === "pending" && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {rejectingId === w.id ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Input
                                  placeholder="Reason (optional)"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  className="max-w-[180px]"
                                  data-testid={`input-reject-reason-${w.id}`}
                                />
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => rejectMutation.mutate({ id: w.id, reason: rejectReason || "Rejected" })}
                                  disabled={rejectMutation.isPending}
                                  data-testid={`button-confirm-reject-${w.id}`}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { setRejectingId(null); setRejectReason(""); }}
                                  data-testid={`button-cancel-reject-${w.id}`}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => payMutation.mutate(w.id)}
                                  disabled={payMutation.isPending}
                                  data-testid={`button-mark-paid-${w.id}`}
                                >
                                  <Check className="h-3 w-3 mr-1" />
                                  Mark Paid
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setRejectingId(w.id)}
                                  data-testid={`button-reject-${w.id}`}
                                >
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
